import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import manualPaymentVerificationService from '../../../services/manualPaymentVerificationService';

const PendingVerificationDashboard = ({ refreshTrigger }) => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingActivations, setPendingActivations] = useState([]);
  const [manualOverrides, setManualOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPendingItems();
  }, [refreshTrigger]);

  const loadPendingItems = async () => {
    try {
      setLoading(true);
      setError('');

      const [payments, activations, overrides] = await Promise.all([
        manualPaymentVerificationService?.getPendingPaymentVerifications(),
        manualPaymentVerificationService?.getPendingActivationRequests(),
        manualPaymentVerificationService?.getManualOverrideQueue()
      ]);

      setPendingPayments(payments?.slice(0, 5) || []);
      setPendingActivations(activations?.slice(0, 5) || []);
      setManualOverrides(overrides?.slice(0, 5) || []);
    } catch (err) {
      setError(err?.message || 'Failed to load pending items');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'high':
        return 'text-orange-600 bg-orange-50';
      case 'medium':
        return 'text-amber-600 bg-amber-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Unverified Payments */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="CreditCard" className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-foreground">Unverified Payments</h3>
          </div>
          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
            {pendingPayments?.length || 0}
          </span>
        </div>
        <div className="p-4 space-y-3">
          {pendingPayments?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No pending payments</p>
          ) : (
            pendingPayments?.map((payment) => (
              <div key={payment?.id} className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{payment?.companyName}</p>
                    <p className="text-xs text-muted-foreground">{payment?.contactEmail}</p>
                  </div>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded">
                    {payment?.paymentMethod?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-foreground">K{payment?.amount?.toFixed(2)}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(payment?.createdAt)?.toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Activation Requests */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="UserPlus" className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-foreground">Activation Requests</h3>
          </div>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
            {pendingActivations?.length || 0}
          </span>
        </div>
        <div className="p-4 space-y-3">
          {pendingActivations?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No pending activations</p>
          ) : (
            pendingActivations?.map((activation) => (
              <div key={activation?.id} className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{activation?.companyName}</p>
                    <p className="text-xs text-muted-foreground">{activation?.contactEmail}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-bold rounded ${
                    activation?.subscriptionStatus === 'suspended' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {activation?.subscriptionStatus?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {activation?.currentTier?.toUpperCase()} • {activation?.activeEmployeeCount} employees
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(activation?.createdAt)?.toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Manual Override Queue */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" className="w-4 h-4 text-red-600" />
            <h3 className="text-sm font-semibold text-foreground">Manual Override Queue</h3>
          </div>
          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
            {manualOverrides?.length || 0}
          </span>
        </div>
        <div className="p-4 space-y-3">
          {manualOverrides?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No manual overrides needed</p>
          ) : (
            manualOverrides?.map((override) => (
              <div key={override?.id} className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{override?.companyName}</p>
                    <p className="text-xs text-muted-foreground">{override?.contactEmail}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-bold rounded ${getPriorityColor(override?.priority)}`}>
                    {override?.priority?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {override?.subscriptionStatus?.toUpperCase()}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(override?.updatedAt)?.toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingVerificationDashboard;